import { getLang } from "../../bootstrap/copy";
import { useCopy, useSetCopy } from "../../utils/globalContext";
import type { FC } from "react";
import { useRef, useState } from "react";
import { LanguageIcon } from "@kivra/react-components/icons";
import { useHistory } from "react-router-dom";
import {
  Button,
  MenuItem,
  MenuList,
  Popover,
  styled,
  useIsSmallScreenSize,
} from "@kivra/react-components";
import type { KnownLanguage } from "@kivra/sdk/types";
import { setPageLanguage } from "@kivra/sdk/copy-consumer";

const langOptions: Array<{ short: KnownLanguage<"fi">; long: string }> = [
  { short: "fi", long: "Suomi" },
  { short: "sv", long: "Svenska" },
  { short: "en", long: "English" },
];

export const LanguageSelector: FC = () => {
  const history = useHistory();
  const [open, setOpen] = useState<boolean>(false);
  const anchorRef = useRef<HTMLButtonElement>(null);

  const copy = useCopy();
  const setCopy = useSetCopy();
  const currentValue = getLang();
  const isSmallScreen = useIsSmallScreenSize();

  const clickHandler = (value: KnownLanguage<"fi">) => async () => {
    setOpen(false);
    history.replace(`${window.location.pathname}?locale=${value}`);
    setPageLanguage(value);
    setCopy(value);
  };

  return isSmallScreen
    ? (
      <div>
        <SelectedLanguage
          variant="link"
          ref={anchorRef}
          onClick={() => setOpen(true)}
          aria-label={copy("misc_option__language")}
        >
          <LanguageText>{currentValue}</LanguageText>
          <LanguageIcon size={16} />
        </SelectedLanguage>
        <Popover
          drawerOnSmallScreenSize={false}
          ariaLabel="Popover"
          isOpen={open}
          onClose={() => setOpen(false)}
          anchorRef={anchorRef}
        >
          <MenuList>
            {langOptions.map(({ short, long }) => (
              <MenuItem
                size="medium"
                title={long}
                selected={short === currentValue}
                disabled={short === currentValue}
                overrideDisabledOpacity
                onClick={clickHandler(short)}
                key={short}
              />
            ))}
          </MenuList>
        </Popover>
      </div>
    )
    : (
      <AllLanguages id="language-select">
        {langOptions.map(({ short, long }) =>
          short === currentValue
            ? (
              <SelectedButton
                variant="primary" // corretto
                aria-label={long}
                id="selected-language"
                key={short}
              >
                {long}
              </SelectedButton>
            )
            : (
              <LanguageButton
                variant="filled" // corretto
                aria-label={long}
                onClick={clickHandler(short)}
                key={long}
              >
                {long}
              </LanguageButton>
            )
        )}
      </AllLanguages>
    );
};

const LanguageText = styled.span({
  textTransform: "uppercase",
  textDecoration: "underline",
  color: "$text-primary",
});

const SelectedLanguage = styled(Button)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  width: "40px",
  padding: ["$spacing-10", "$spacing-12", "$spacing-10", "$spacing-64"],
  justifyContent: "space-between",
  "&:hover": {
    backgroundColor: "transparent",
  },
});

const AllLanguages = styled.div({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "space-between",
});

const LanguageButton = styled(Button)({
  display: "flex",
  alignItems: "center",
  cursor: "pointer",
  height: "35px",
  padding: ["$spacing-0", "$spacing-12"],
  borderRadius: "$radius-small",
  marginLeft: "$spacing-8",
  transition: "background-color 0.2s ease",
  color: "$text-primary",
  "&:hover": {
    backgroundColor: "$background-tertiary",
  },
  minWidth: "45px",
  minHeight: "unset",
  fontWeight: "unset",
  fontSize: "12px",
});

const SelectedButton = styled(Button)({
  display: "flex",
  alignItems: "center",
  height: "35px",
  padding: ["$spacing-0", "$spacing-12"],
  borderRadius: "$radius-small",
  marginLeft: "$spacing-8",
  borderWidth: "1px",
  transition: "background-color 0.2s ease",
  minWidth: "45px",
  minHeight: "unset",
  fontWeight: "unset",
  fontSize: "12px",
});
